import { createContext, useEffect, useState, ReactNode, useMemo } from 'react'
import { useRouter } from 'next/router'

import { SanityPrintShopBase } from '@data/sanity/queries/types/shop'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  getPrintShopCookie,
  removePrintShopCookie,
  setPrintShopCookie,
} from './cookie'
import { getPrintShopPageUrl } from './url'

interface PrintShopContextProps {
  printShops: SanityPrintShopBase[]
  printShop: SanityPrintShopBase | null
  printShopInstitutionId: string | null
  setPrintShop: (printShop: SanityPrintShopBase | null) => void
}

interface PrintShopContextProviderProps {
  site: SanitySiteFragment
  children: ReactNode
}

export const PrintShopContext = createContext<PrintShopContextProps>({
  printShops: [],
  printShop: null,
  printShopInstitutionId: null,
  setPrintShop: () => {},
})

export const PrintShopContextProvider = ({
  site,
  children,
}: PrintShopContextProviderProps) => {
  const router = useRouter()

  const [printShop, setPrintShop] = useState<SanityPrintShopBase | null>(null)

  const printShopInstitutionId = useMemo<string | null>(() => {
    if (!printShop) {
      return null
    }

    return printShop.institution.id
  }, [printShop])

  useEffect(() => {
    // Load print shop from cookie
    const printShopCookie = getPrintShopCookie()

    if (!printShopCookie) {
      return
    }

    setPrintShop(printShopCookie)
  }, [])

  useEffect(() => {
    // Make sure that print shop cookie matches currently open print shop
    if (!printShop) {
      removePrintShopCookie()
      return
    }

    const printShopCookie = getPrintShopCookie()

    if (printShopCookie && printShopCookie.slug === printShop.slug) {
      return
    }

    setPrintShopCookie(printShop)

    // Update current URL when print shop changes
    const printShopPageUrl = getPrintShopPageUrl(router, printShop)

    if (printShopPageUrl && printShopPageUrl !== router.asPath) {
      router.push(printShopPageUrl)
    }
  }, [printShop, router])

  return (
    <PrintShopContext.Provider
      value={{
        printShops: site.shops,
        printShop,
        printShopInstitutionId,
        setPrintShop,
      }}
    >
      {children}
    </PrintShopContext.Provider>
  )
}
